/**
 * Cliente del planner-service (servicio aparte, default :3020): la conexión de un seller con Lepton
 * Planner (xcons-docs#542, Fase 9). Autentica con el MISMO `access_token` del panel (Bearer): el
 * servicio resuelve la sesión contra user_sessions, como conversation-service.
 */
import axios from "axios";
import { runtimeValue } from "@/config/runtime-config";
import { getAccessToken } from "./conversations.api";

/**
 * URL del planner-service, de la config de runtime que inyecta nginx (`runtime-config.template.js`).
 * **Sin fallback**, por lo mismo que `CONVERSATIONS_API_URL`: `""` quiere decir "no hay servicio",
 * y la sección de Planner no aparece. En el dev server de Vite la URL sale de
 * `VITE_PLANNER_API_URL` y se va por el proxy `/planner-api`.
 */
export const PLANNER_API_URL =
  runtimeValue("PLANNER_API_URL") || import.meta.env.VITE_PLANNER_API_URL || "";

/** `false` si no hay URL configurada: la pantalla no muestra la sección. */
export const isPlannerConfigured = (): boolean => PLANNER_API_URL !== "";

/** Se decide por el dato, no por el modo de build (ver `HTTP_BASE` de conversations.api). */
const HTTP_BASE = runtimeValue("PLANNER_API_URL") || "/planner-api";

const client = axios.create({ baseURL: HTTP_BASE });
client.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${getAccessToken()}`;
  return config;
});

export interface PlannerConnection {
  sellerCode: string;
  /** El seller está habilitado para Planner (lo decide el servicio). Sin eso, la sección no aparece. */
  enabled: boolean;
  connected: boolean;
  email?: string;
  /** La guardada: el servicio la devuelve a quien puede cambiarla, como el usuario de Lepton. */
  password?: string;
  apiToken?: string | null;
  hasApiToken?: boolean;
  branchId?: number | null;
  branchName?: string | null;
  connectedAt?: string;
  /** Token, sesión y login dieron 401 desde esta fecha. */
  lastAuthErrorAt?: string | null;
  /** El usuario de la integración puede mover las entregas de este seller. */
  xconsPermissionOk?: boolean;
  subscriptionStatus?: string | null;
}

export interface PlannerBranch {
  id: number;
  name: string;
  hasEdgebanding: boolean;
}

export interface PlannerCredentials {
  email: string;
  /** Vacía al editar = la guardada. */
  password?: string;
  /** Vacío = no cambia. */
  apiToken?: string;
}

const path = (sellerCode: string) =>
  `/sellers/${encodeURIComponent(sellerCode)}/planner-connection`;

/** Un objeto y no HTML: si la base apunta mal, nginx devuelve el index del SPA con 200. */
function asConnection(data: unknown, sellerCode: string): PlannerConnection {
  if (data && typeof data === "object" && "connected" in data) {
    return data as PlannerConnection;
  }
  console.error("[planner.api] la conexión no vino como objeto", data);
  return { sellerCode, enabled: false, connected: false };
}

export async function getPlannerConnection(
  sellerCode: string,
): Promise<PlannerConnection> {
  const { data } = await client.get(path(sellerCode));
  return asConnection(data, sellerCode);
}

export async function getPlannerBranches(
  sellerCode: string,
  creds: PlannerCredentials,
): Promise<PlannerBranch[]> {
  const { data } = await client.post(`${path(sellerCode)}/branches`, creds);
  return Array.isArray(data) ? data : [];
}

export async function savePlannerConnection(
  sellerCode: string,
  payload: PlannerCredentials & { branchId: number; removeApiToken?: boolean },
): Promise<PlannerConnection> {
  const { data } = await client.put(path(sellerCode), payload);
  return asConnection(data, sellerCode);
}

export async function deletePlannerConnection(
  sellerCode: string,
): Promise<void> {
  await client.delete(path(sellerCode));
}
